import { useState, useEffect } from 'react';
import { HOME_PATH } from '@/lib/paths';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowLeft, Calendar, TrendingUp, Moon, Settings, Heart, Apple, Baby } from 'lucide-react';
import MenstrualCalendar from '../MenstrualCalendar';
import CyclePredictor from '../CyclePredictor';
import DynamicPhaseIndicator from '../DynamicPhaseIndicator';
import TrendsAnalysis from '../TrendsAnalysis';
import CycleSettings from './CycleSettings';
import { TherappiSupportCard } from '../journey/TherappiSupportCard';
import { NutritionalGuidanceCard } from '../journey/NutritionalGuidanceCard';
import TherapyTokenGift from './TherapyTokenGift';
import AppointmentManager from './AppointmentManager';
import { calculateLunarPhase, calculateCycleDay } from '@/utils/lunarPhases';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface MenstrualTrackerProps {
  onBack: () => void;
  onJourneyChange?: (journey: 'menstrual' | 'pregnancy' | 'perimenopause' | 'menopause') => void;
}

export default function MenstrualTracker({ onBack, onJourneyChange }: MenstrualTrackerProps) {
  const [cycleStartDate, setCycleStartDate] = useState<Date>(new Date());
  const [cycleLength, setCycleLength] = useState(28);
  const [periodEndDate, setPeriodEndDate] = useState<Date | undefined>();
  const [showTrends, setShowTrends] = useState(false);
  const [isPregnancyMode, setIsPregnancyMode] = useState(false);
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [showPeriodDialog, setShowPeriodDialog] = useState(false);
  const [showPregnancyDialog, setShowPregnancyDialog] = useState(false);
  const [newPeriodDate, setNewPeriodDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadCycleData();
  }, []);

  const loadCycleData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('daily_checkins')
        .select('cycle_start_date, cycle_length, period_end_date, date')
        .eq('user_id', user.id)
        .not('cycle_start_date', 'is', null)
        .order('date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
        // Parse dates correctly - split YYYY-MM-DD and create local date
        if (data.cycle_start_date) {
          const [year, month, day] = data.cycle_start_date.split('-').map(Number);
          const localDate = new Date(year, month - 1, day);
          setCycleStartDate(localDate);
        }
        if (data.cycle_length) {
          setCycleLength(data.cycle_length);
        }
        if (data.period_end_date) {
          const [year, month, day] = data.period_end_date.split('-').map(Number);
          const localDate = new Date(year, month - 1, day);
          setPeriodEndDate(localDate);
        }
        
        // Load pregnancy mode settings
        const pregnancyMode = (data as any).pregnancy_mode || false;
        const dueDateStr = (data as any).pregnancy_due_date;
        setIsPregnancyMode(pregnancyMode);
        if (dueDateStr) {
          const [year, month, day] = dueDateStr.split('-').map(Number);
          setDueDate(new Date(year, month - 1, day));
        }
      }
    } catch (error) {
      console.error('Error loading cycle data:', error);
    }
  };

  const handleSaveCycleSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Not authenticated",
          description: "Please log in to save your cycle data.",
          variant: "destructive",
        });
        return;
      }

      const todayKey = new Date().toISOString().split('T')[0];
      const formattedData = {
        user_id: user.id,
        date: todayKey,
        cycle_start_date: cycleStartDate.toISOString().split('T')[0],
        cycle_length: cycleLength,
        period_end_date: periodEndDate?.toISOString().split('T')[0]
      };

      const { error } = await supabase
        .from('daily_checkins')
        .upsert(formattedData, {
          onConflict: 'user_id,date'
        });

      if (error) {
        throw error;
      }
      
      toast({
        title: "✅ Cycle settings saved",
        description: "Your menstrual cycle data is now persistent across all journeys.",
      });
    } catch (error) {
      console.error('Error saving cycle settings:', error);
      toast({
        title: "Error saving cycle settings",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDateSelect = async (date: Date, cycleDay: number, lunarPhase: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const dateStr = date.toISOString().split('T')[0];

      await supabase
        .from('daily_checkins')
        .upsert({
          user_id: user.id,
          date: dateStr,
          menstrual_cycle_day: cycleDay,
          lunar_phase: lunarPhase.phase,
          cycle_start_date: cycleStartDate.toISOString().split('T')[0],
          cycle_length: cycleLength,
          period_end_date: periodEndDate?.toISOString().split('T')[0]
        }, {
          onConflict: 'user_id,date'
        });

      toast({
        title: "Saved!",
        description: "Your cycle data has been updated.",
      });
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  const currentCycleDay = calculateCycleDay(cycleStartDate, new Date());

  const handleEnterPeriod = () => {
    setShowPeriodDialog(true);
  };

  const handleSaveNewPeriod = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const newDate = parseISO(newPeriodDate);
      setCycleStartDate(newDate);

      const { error } = await supabase
        .from('daily_checkins')
        .upsert({
          user_id: user.id,
          date: newPeriodDate,
          cycle_start_date: newPeriodDate,
          cycle_length: cycleLength
        }, {
          onConflict: 'user_id,date'
        });

      if (error) throw error;

      toast({
        title: "Period recorded",
        description: "Your cycle has been updated with the new period start date.",
      });

      setShowPeriodDialog(false);
      loadCycleData();
    } catch (error) {
      console.error('Error saving period:', error);
      toast({
        title: "Error",
        description: "Failed to save period data.",
        variant: "destructive",
      });
    }
  };

  const handleSwitchToPregnancy = () => {
    setShowPregnancyDialog(true);
  };

  const handleConfirmPregnancy = () => {
    setShowPregnancyDialog(false);
    navigate(HOME_PATH);
    toast({
      title: "Switch to Pregnancy Tracking",
      description: "Head to Cycle & Lunar Hub and select 'Pregnancy Tracking' to monitor your journey.",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-3xl font-bold">Menstrual Cycle Tracker</h2>
          <p className="text-muted-foreground">Track your cycle and align with lunar wisdom</p>
        </div>
      </div>

      {/* Educational Card */}
      <TherapyTokenGift journeyType="menstrual" />
      
      <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Moon className="h-5 w-5" />
            Why Track Your Cycle?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm">
            Understanding your menstrual cycle helps you predict periods, plan your life around hormonal changes, 
            and recognise patterns in your mood, energy, and symptoms. By combining cycle tracking with lunar phases, 
            you gain deeper insights into your natural rhythms.
          </p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-semibold text-primary mb-2">What You'll Discover:</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• When your next period is due</li>
                <li>• Your fertility window</li>
                <li>• Energy patterns by phase</li>
                <li>• Symptom trends over time</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-primary mb-2">How This Helps:</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Plan important events</li>
                <li>• Optimise workouts</li>
                <li>• Manage expectations</li>
                <li>• Honor your body's wisdom</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Phase */}
      <DynamicPhaseIndicator
        currentCycleDay={currentCycleDay}
        cycleLength={cycleLength}
      />

      {/* Main Tabs */}
      <Tabs defaultValue="calendar" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="calendar">
            <Calendar className="h-4 w-4 mr-2" />
            Calendar
          </TabsTrigger>
          <TabsTrigger value="predictions">
            <TrendingUp className="h-4 w-4 mr-2" />
            Predictions
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="trends">
            <Moon className="h-4 w-4 mr-2" />
            Trends
          </TabsTrigger>
          <TabsTrigger value="support">
            <Heart className="h-4 w-4 mr-2" />
            Support
          </TabsTrigger>
          <TabsTrigger value="nutrition">
            <Apple className="h-4 w-4 mr-2" />
            Nutrition
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="space-y-4">
          <MenstrualCalendar
            onDateSelect={handleDateSelect}
            initialCycleStartDate={cycleStartDate}
            initialCycleLength={cycleLength}
            periodEndDate={periodEndDate}
            onPeriodEndDateChange={setPeriodEndDate}
            onCycleDataChange={(start, length) => {
              setCycleStartDate(start);
              setCycleLength(length);
            }}
            onSaveCycleSettings={handleSaveCycleSettings}
          />
        </TabsContent>

        <TabsContent value="predictions" className="space-y-4">
          <CyclePredictor
            cycleStartDate={cycleStartDate}
            cycleLength={cycleLength}
            periodEndDate={periodEndDate}
            isPregnancyMode={isPregnancyMode}
            dueDate={dueDate}
            onEnterPeriod={handleEnterPeriod}
            onSwitchToPregnancy={handleSwitchToPregnancy}
          />
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <CycleSettings 
            onSettingsChange={loadCycleData}
            onJourneyChange={onJourneyChange}
          />
          <AppointmentManager journeyType="menstrual" />
        </TabsContent>

        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Wellness Trends & Patterns</CardTitle>
              <CardDescription>
                Analyse your cycle patterns and get personalised insights
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => setShowTrends(true)} className="w-full">
                <TrendingUp className="h-4 w-4 mr-2" />
                View Detailed Analysis
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="support" className="space-y-4">
          <TherappiSupportCard journeyType="menstrual" />
        </TabsContent>

        <TabsContent value="nutrition" className="space-y-4">
          <NutritionalGuidanceCard focusArea="menstrual" />
        </TabsContent>
      </Tabs>

      {showTrends && <TrendsAnalysis onClose={() => setShowTrends(false)} />}

      {/* Period Entry Dialog */}
      <Dialog open={showPeriodDialog} onOpenChange={setShowPeriodDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enter Period Start Date</DialogTitle>
            <DialogDescription>
              When did your period actually start?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="period-date">Period Start Date</Label>
              <Input
                id="period-date"
                type="date"
                value={newPeriodDate}
                onChange={(e) => setNewPeriodDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <Button onClick={handleSaveNewPeriod} className="w-full">
              Save Period Start Date
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pregnancy Confirmation Dialog */}
      <Dialog open={showPregnancyDialog} onOpenChange={setShowPregnancyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Baby className="h-5 w-5" />
              Think You Might Be Pregnant?
            </DialogTitle>
            <DialogDescription>
              If you believe you may be pregnant, we recommend switching to our Pregnancy & Postpartum tracker 
              for specialised support throughout your journey.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-pink-500/10 border border-pink-500/20 rounded-lg">
              <h4 className="font-medium mb-2">What happens next?</h4>
              <ul className="text-sm space-y-2 text-muted-foreground">
                <li>• Return to the Cycle & Lunar Hub</li>
                <li>• Select "Pregnancy Tracking"</li>
                <li>• Enter your expected due date</li>
                <li>• Track baby's development week-by-week</li>
              </ul>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setShowPregnancyDialog(false)} variant="outline" className="flex-1">
                Not Yet
              </Button>
              <Button onClick={handleConfirmPregnancy} className="flex-1">
                <Baby className="h-4 w-4 mr-1" />
                Switch to Pregnancy
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
